import { USD_CURRENCY_CODE } from 'constants/index';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import type { Field, Item, Price, SingleOffer, Tax } from 'models';
import type { RootState } from 'reducers/types';
import {
  getCountryCurrency,
  getCurrency,
  getOffer,
  getRecipient,
  getSelectedOffer,
  isPaymentProcessWithNobookingCta,
} from 'selectors';

export const getOrder = (state: RootState) => {
  const {
    entities: { order },
  } = state;
  return order;
};

export const getOrderOffer = (state: RootState) => {
  const offerId = get(state, 'entities.order.offer.id');
  return getOffer(state, offerId);
};

export const hasOrder = (state: RootState) => {
  const {
    entities: { order },
  } = state;

  return !!order?.id;
};

export const getPaymentOption = (state: RootState): string => {
  const {
    entities: { order: { offer: { paymentOption = '' } = {} } = {} },
  } = state;

  return paymentOption;
};

export const orderHasOwner = (state: RootState) => {
  const {
    entities: {
      order: { hasOwner },
    },
  } = state;
  return hasOwner || false;
};

export const getOrderAmount = (state: RootState) => {
  const {
    entities: {
      order: { recipient: { items = [] } = {} },
    },
  } = state;
  const accumulateAmount = (total: number, item: Item) => total + item.amount;

  return items.reduce(accumulateAmount, 0);
};

export const getSenderCountry = (state: RootState) => {
  const {
    entities: {
      order: { sender: { fields = [] } = {} },
    },
  } = state;
  const countryField = fields.find(field => field.id === 'country');

  return countryField?.value ? (countryField.value as string) : '';
};

export const getSenderCurrency = (state: RootState) => {
  const DEFAULT_CURRENCY = USD_CURRENCY_CODE;
  const country = getSenderCountry(state);
  let currency;
  if (country) {
    const currencyCode = getCountryCurrency(state, country as string);
    currency = getCurrency(state, currencyCode);
    if (isEmpty(currency)) {
      currency = getCurrency(state, DEFAULT_CURRENCY);
    }
  }

  return currency;
};

export const getSenderField = (state: RootState, field: string) => {
  const {
    entities: { order: { sender: { fields = [] } = {} } = {} },
  } = state;

  const senderField = fields.find(f => f.id === field);
  if (senderField) {
    return senderField.value;
  }
};

export const getSenderEmail = (state: RootState) => {
  return getSenderField(state, 'email');
};

export const getOrderReturnUrl = (state: RootState) => {
  const {
    entities: { order: { returnUrl = '' } = {} },
  } = state;

  return returnUrl;
};

export const paymentIsFromAgents = (state: RootState) => {
  const {
    entities: { order },
  } = state;

  return order?.provider === 'agents';
};

export const getNobookingCtaUrl = (state: RootState) => {
  const { website = '' } = getRecipient(state);
  const {
    entities: {
      order: { metadata: { returnNobookingCta } = {} },
    },
  } = state;

  return isPaymentProcessWithNobookingCta(state) ? returnNobookingCta : website;
};

export const getNobookingCtaName = (state: RootState) => {
  const { website = '' } = getRecipient(state);
  const {
    entities: {
      order: { metadata: { returnNobookingCta, returnNobookingCtaName } = {} },
    },
  } = state;

  return isPaymentProcessWithNobookingCta(state)
    ? returnNobookingCtaName || returnNobookingCta
    : website;
};

export const isReadOnlyOrderSectionField = (
  state: RootState,
  field: string,
) => {
  const {
    entities: { order = {} },
  } = state;

  const [sectionId, ...rest] = field.split('_');
  const fieldId = rest.join('_');

  const sectionFields = get(order, `${sectionId}.fields`) || [];
  if (!sectionFields) {
    return false;
  }

  const sectionField = (sectionFields as Field[]).find(
    sectionField => sectionField.id === fieldId,
  );
  if (!sectionField) {
    return false;
  }

  return !!sectionField.readOnly;
};

export const getOrderSectionFieldValue = (field: string, state: RootState) => {
  const {
    entities: { order = {} },
  } = state;

  const [sectionId, ...rest] = field.split('_');
  const fieldId = rest.join('_');

  const sectionFields = get(order, `${sectionId}.fields`) || [];
  if (!sectionFields) {
    return null;
  }

  const sectionField = (sectionFields as Field[]).find(
    sectionField => sectionField.id === fieldId,
  );
  if (!sectionField) {
    return null;
  }

  return sectionField.value;
};

export const getTaxes = (state: RootState): Tax[] => {
  const {
    entities: { order: { offer: { taxes = [] } = {} } = {} },
  } = state;

  if (taxes.length === 0) {
    return [];
  }

  return taxes;
};

export const getOrderOriginalAmount = (state: RootState) => {
  const offer = getSelectedOffer(state);
  const { price: { value = 0, currency } = {} } = offer;

  return { value, currency };
};

export const getOrderCouponDiscount = (state: RootState) => {
  const {
    entities: {
      order: { extraFees = [] },
    },
  } = state;

  const couponExtraFees = extraFees.find(
    extraFee =>
      extraFee.category === 'discount' &&
      extraFee.subcategory.startsWith('coupon_'),
  );
  const value = couponExtraFees?.price?.value || 0;
  const currency = couponExtraFees?.price?.currency?.code;

  return { value, currency };
};

export const getOrderTotalAmount = (state: RootState) => {
  const orderOriginal = getOrderOriginalAmount(state);
  const couponDiscount = getOrderCouponDiscount(state);

  const value = orderOriginal.value + couponDiscount.value;
  const currency = orderOriginal.currency || couponDiscount.currency;

  return { value, currency };
};

export const getTotalTaxes = (state: RootState) => {
  const {
    entities: { order: { offer: { taxes = [] } = {} } = {} },
  } = state;

  return taxes.reduce((total, tax) => total + +tax.amount, 0);
};
export const orderIncludesTaxes = (state: RootState) => {
  return getTotalTaxes(state) > 0;
};

export const getOrderPrice = (state: RootState) => {
  const offer = getOrderOffer(state);

  return (offer as unknown as SingleOffer<Price>).price;
};

export const getOrderPriceWithTaxes = (state: RootState) => {
  const price = getOrderPrice(state);
  const taxes = getTotalTaxes(state);

  if (!taxes) {
    return price;
  }

  return { value: price.value + taxes, currency: price.currency };
};
